import React, { useState, useEffect, useRef } from 'react'
import { Upload, FileSpreadsheet, Check, AlertTriangle, Loader2, History, X } from 'lucide-react'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL

export default function ImportProvisions({ tenantId }) {
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState(null)
  const [history, setHistory] = useState([])
  const [showHistory, setShowHistory] = useState(false)
  const fileRef = useRef(null)

  async function fetchHistory() {
    try {
      const res = await fetch(`${API}/api/provisions/imports/${tenantId}`)
      const data = await res.json()
      setHistory(data.imports || [])
    } catch {}
  }

  useEffect(() => { if (tenantId) fetchHistory() }, [tenantId])

  async function handleUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setResult(null)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('tenant_id', tenantId)
      const res = await fetch(`${API}/api/provisions/import`, { method: 'POST', body: form })
      const data = await res.json()
      if (data.success) {
        setResult(data)
        toast.success(`${data.nb_mis_a_jour} provisions mises a jour${data.nb_dossiers_ecarts ? ` — ${data.nb_dossiers_ecarts} dossier(s) avec écarts compagnie` : ''}`)
        fetchHistory()
      } else {
        toast.error(data.detail || 'Erreur import')
      }
    } catch (err) {
      toast.error('Erreur lors de l\'import')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  function fmtDate(d) {
    if (!d) return '--'
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }
    catch { return d }
  }

  return (
    <div data-testid="import-provisions">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-sm text-slate-800 flex items-center gap-2">
          <FileSpreadsheet size={16} className="text-emerald-500" />
          Import RI compagnie — contrôle des anomalies et des provisions
        </h3>
        <div className="flex gap-2">
          <button onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            data-testid="history-btn">
            <History size={12} /> Historique ({history.length})
          </button>
        </div>
      </div>

      {/* Upload zone */}
      <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-indigo-300 transition-colors mb-4">
        <input type="file" ref={fileRef} onChange={handleUpload} accept=".xlsx,.xls,.csv" className="hidden" id="provision-upload" data-testid="provision-file-input" />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 size={24} className="animate-spin text-indigo-500" />
            <p className="text-sm text-slate-500">Analyse du relevé en cours...</p>
            <p className="text-xs text-slate-400">Lecture du fichier, rapprochement des dossiers, contrôle des anomalies et des provisions</p>
          </div>
        ) : (
          <label htmlFor="provision-upload" className="cursor-pointer">
            <Upload size={24} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600 font-medium">Importer un RI compagnie (relevé de sinistralité)</p>
            <p className="text-xs text-slate-400 mt-1">Excel (.xlsx) ou CSV — max 10 Mo. Flotte : les provisions saisies par les gestionnaires sont contrôlées, pas écrasées.</p>
          </label>
        )}
      </div>

      {/* Result */}
      {result && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4" data-testid="import-result">
          <div className={`grid gap-4 mb-4 ${result.nb_dossiers_ecarts != null ? 'grid-cols-5' : 'grid-cols-4'}`}>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-lg font-bold text-slate-800">{result.nb_lignes}</p>
              <p className="text-[10px] text-slate-500">Lignes lues</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-lg font-bold text-green-700">{result.nb_matched}</p>
              <p className="text-[10px] text-green-600">Sinistres matches</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-lg font-bold text-blue-700">{result.nb_mis_a_jour}</p>
              <p className="text-[10px] text-blue-600">Provisions mises a jour</p>
            </div>
            <div className={`text-center p-3 rounded-lg ${result.nb_inconnus > 0 ? 'bg-amber-50' : 'bg-slate-50'}`}>
              <p className={`text-lg font-bold ${result.nb_inconnus > 0 ? 'text-amber-700' : 'text-slate-500'}`}>{result.nb_inconnus}</p>
              <p className={`text-[10px] ${result.nb_inconnus > 0 ? 'text-amber-600' : 'text-slate-400'}`}>Inconnus</p>
            </div>
            {result.nb_dossiers_ecarts != null && (
              <div className={`text-center p-3 rounded-lg ${result.nb_dossiers_ecarts > 0 ? 'bg-orange-50' : 'bg-slate-50'}`} data-testid="import-ecarts-cie">
                <p className={`text-lg font-bold ${result.nb_dossiers_ecarts > 0 ? 'text-orange-700' : 'text-slate-500'}`}>{result.nb_dossiers_ecarts}</p>
                <p className={`text-[10px] ${result.nb_dossiers_ecarts > 0 ? 'text-orange-600' : 'text-slate-400'}`}>Écarts compagnie</p>
              </div>
            )}
          </div>

          {result.dossiers_ecarts?.length > 0 && (
            <div className="border border-orange-200 rounded-lg overflow-hidden mb-3" data-testid="import-anomalies">
              <div className="bg-orange-50 px-3 py-2 flex items-center gap-2 border-b border-orange-200">
                <AlertTriangle size={14} className="text-orange-500" />
                <span className="text-xs font-medium text-orange-700">Anomalies détectées — {result.dossiers_ecarts.length} dossier(s)</span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-orange-50/50">
                    <th className="text-left px-3 py-1.5 text-orange-600">Dossier</th>
                    <th className="text-left px-3 py-1.5 text-orange-600">Contrôle</th>
                    <th className="text-left px-3 py-1.5 text-orange-600">Gestionnaire</th>
                    <th className="text-left px-3 py-1.5 text-orange-600">Relevé Cie</th>
                  </tr>
                </thead>
                <tbody>
                  {result.dossiers_ecarts.map(d => d.anomalies.map((a, i) => (
                    <tr key={`${d.sinistre_id}-${i}`} className="border-t border-orange-100 hover:bg-orange-50/40 cursor-pointer" onClick={() => window.open(`/c2/sinistre/${d.sinistre_id}`, '_blank')} data-testid="import-anomalie-row">
                      <td className="px-3 py-1.5 font-mono">{i === 0 ? (d.numero_sinistre_client || d.numero_compagnie) : ''}</td>
                      <td className="px-3 py-1.5 font-medium text-slate-700">{a.label}</td>
                      <td className="px-3 py-1.5">{a.attendu}</td>
                      <td className="px-3 py-1.5 text-orange-700 font-medium">{a.releve}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          )}

          {result.sinistres_inconnus?.length > 0 && (
            <div className="border border-amber-200 rounded-lg overflow-hidden">
              <div className="bg-amber-50 px-3 py-2 flex items-center gap-2 border-b border-amber-200">
                <AlertTriangle size={14} className="text-amber-500" />
                <span className="text-xs font-medium text-amber-700">Sinistres non reconnus</span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-amber-50/50">
                    <th className="text-left px-3 py-1.5 text-amber-600">N Compagnie</th>
                    <th className="text-right px-3 py-1.5 text-amber-600">Provision</th>
                  </tr>
                </thead>
                <tbody>
                  {result.sinistres_inconnus.map((s, i) => (
                    <tr key={i} className="border-t border-amber-100">
                      <td className="px-3 py-1.5 font-mono">{s.numero_compagnie}</td>
                      <td className="px-3 py-1.5 text-right">{s.provision?.toLocaleString('fr-FR')} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* History */}
      {showHistory && history.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b">
            <span className="text-xs font-medium text-slate-600">Historique des imports</span>
            <button onClick={() => setShowHistory(false)} className="text-slate-400 hover:text-slate-600"><X size={12} /></button>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50/50 border-b">
                <th className="text-left px-3 py-1.5">Fichier</th>
                <th className="text-left px-3 py-1.5">Date</th>
                <th className="text-center px-3 py-1.5">MAJ</th>
                <th className="text-center px-3 py-1.5">Inconnus</th>
                <th className="text-center px-3 py-1.5">Statut</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-mono truncate max-w-[200px]">{h.nom_fichier}</td>
                  <td className="px-3 py-1.5 text-slate-500">{fmtDate(h.date_import)}</td>
                  <td className="px-3 py-1.5 text-center text-green-600 font-medium">{h.nb_mis_a_jour}</td>
                  <td className="px-3 py-1.5 text-center">{h.nb_inconnus > 0 ? <span className="text-amber-600 font-medium">{h.nb_inconnus}</span> : '0'}</td>
                  <td className="px-3 py-1.5 text-center">
                    {h.statut === 'termine' && <span className="text-green-500"><Check size={12} /></span>}
                    {h.statut === 'erreur' && <span className="text-red-500"><AlertTriangle size={12} /></span>}
                    {h.statut === 'en_cours' && <Loader2 size={12} className="animate-spin text-slate-400" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
